import { ReactElement, useEffect, useState, ChangeEvent, useContext } from "react";
import { useAsyncFetch, useFetch } from "../../../../system/useFetch";
import { IAxisEntity, IEditionResponse } from "../../types";
import { Card, IconButton, List, ListItemButton, ListItemText, TextField, Tooltip } from "@mui/material";
import { cloneObject } from "../../../../system/utils";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { addAlert } from "../../../../actions";
import { useDispatch } from "react-redux";
import Filter from "./filter";
import { SystemContext } from "../../../../context/systemContext";

const AxisConfig = (): ReactElement => {
  const dispatch = useDispatch<any>();
  const systemContext = useContext(SystemContext);
  const [filter, setFilter] = useState<string>("");
  const [selectedAxis, setSelectedAxis] = useState<IAxisEntity | null>(null);
  const [axes, setAxes] = useState<IAxisEntity[]>([]);
  const [execCommand] = useAsyncFetch<IEditionResponse>();
  const { data } = useFetch<IAxisEntity[]>(
    {
      module: "acquisition",
      query: "getAxesEntities",
      payload: {}
    });

  useEffect(() => {
    setAxes((data ?? []).sort((a, b) => a.name > b.name ? 1 : a.name < b.name ?  -1 : 0));
  }, [data]);

  const gridStyle = {
    gridTemplateColumns: "400px 400px",
    gridTemplateRows: "1fr",
    justifyItems: "stretch",
    alignItems: "stretch"
  };

  const handleChange = (field: keyof IAxisEntity, value: any) => {
    if (selectedAxis) {
      setSelectedAxis({ ...selectedAxis, [field]: value });
    }
  };

  const onNewClick = () => {
    setSelectedAxis({
      name: "",
      minimum: 0,
      maximum: 100,
      unit: ""
    });

  };

  const onResetClick = () => {
    if (!selectedAxis) {
      return;
    }

    if (!selectedAxis.axisID) {
      setSelectedAxis(null);
    } else {
      const found = axes?.find(x => x.axisID === selectedAxis.axisID);
      if (found) {
        setSelectedAxis(cloneObject(found));
      }
    }

  };

  const onSaveClick = () => {
    if (selectedAxis) {
      execCommand({
        module: "acquisition",
        query: "insertOrUpdateEntities",
        payload: {
          entity: "AXIS",
          action: selectedAxis.axisID ? "UPDATE" : "INSERT",
          data: {
            ...selectedAxis
          }
        }
      }).then(response => {
        if (response && response.result) {
          if (!selectedAxis.axisID) {
            const newItem = { ...selectedAxis, axisID: response.editedID };
            setAxes([...axes, newItem].sort((a, b) => a.name > b.name ? 1 : a.name < b.name ?  -1 : 0));
            setSelectedAxis(newItem);

          } else {
            const found = axes?.find(x => x.axisID === selectedAxis.axisID);
            if (found) {
              found.name = selectedAxis.name;
              found.minimum = selectedAxis.minimum;
              found.maximum = selectedAxis.maximum;
              found.unit = selectedAxis.unit;

              setAxes([...axes].sort((a, b) => a.name > b.name ? 1 : a.name < b.name ?  -1 : 0));
            }
          }

          dispatch(addAlert({ message: "The axis was saved!", severity: "success", hash: undefined }));
        } else {
          dispatch(addAlert({ message: response?.message ?? "ERROR", severity: "error", hash: undefined }));
        }
      });
    }
  };

  const onDeleteClick = () => {
    if (selectedAxis && selectedAxis.axisID) {
      systemContext.showMessage({
          title: "Delete",
          color: "danger",
          message: `Do you want to delete the Axis "${selectedAxis.name}"?`,
          callback: () => {
            execCommand({
              module: "acquisition",
              query: "insertOrUpdateEntities",
              payload: {
                entity: "AXIS",
                action: "DELETE",
                data: {
                  axisID: selectedAxis.axisID
                }
              }
            }).then(response => {
              if (response && response.result) {
                dispatch(addAlert({ message: "The axis was deleted!", severity: "error", hash: undefined }));
                setAxes(axes?.filter(x => x.axisID !== selectedAxis.axisID));
                setSelectedAxis(null);
              } else {
                dispatch(addAlert({ message: response?.message ?? "ERROR", severity: "error", hash: undefined }));
              }
            });
          }
        });
    }
  };

  return (
    <div className="display-flex-row justify-content-center">
      <div className="display-grid" style={gridStyle}>
        <Card className="m-1">
          <CardHeader title="Axes"
            action={
              <>
                <Tooltip title="Add">
                  <IconButton aria-label="add" color="info" onClick={onNewClick}>
                    <AddIcon/>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <span>
                    <IconButton aria-label="delete" color="error" disabled={selectedAxis === null}
                      onClick={onDeleteClick}>
                      <DeleteIcon/>
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            }/>
          <CardContent>
            <div className="display-grid" style={{ gridTemplateRows: "auto auto" }}>
              <Filter filter={filter} setFilter={setFilter} />
              <div style={{ overflowY: "auto", marginTop: ".5em", height: "600px", maxHeight: "600px" }}>
                <List>
                  {axes && axes.filter(x => filter === "" || x.name.toLowerCase().includes(filter)).map(axis => {

                    return (
                      <ListItemButton key={`axis-${axis.axisID}`}
                        selected={selectedAxis !== null && selectedAxis.axisID === axis.axisID}
                        onClick={() => setSelectedAxis(cloneObject(axis))}>
                        <ListItemText primary={
                          <div className="display-flex-row">
                            <p className="flex-grow-1">{`${axis.name} ${axis.unit && axis.unit !== "" ? `[${axis.unit}]` : ""}`}</p>
                            <p>{`[${axis.minimum}, ${axis.maximum}]`}</p>
                          </div>
                        }/>
                      </ListItemButton>
                    );
                  })}
                </List>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="m-1">
          <CardHeader title="Detail"
            action={
              <>
                <Tooltip title="Save">
                  <span>
                    <IconButton aria-label="save" color="success" onClick={onSaveClick}
                      disabled={selectedAxis === null}>
                      <SaveIcon/>
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reset">
                  <span>
                    <IconButton aria-label="cancel" onClick={onResetClick} disabled={selectedAxis === null}>
                      <RefreshIcon/>
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            }/>
          <CardContent>
            <div style={{ overflowY: "auto", height: "600px" }}>
              {selectedAxis &&
                <div className="display-flex-column">
                  <TextField
                    className="m-1"
                    variant="standard"
                    placeholder={"Name"}
                    label={"Name"}
                    value={selectedAxis.name}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange ("name", event.target.value)}/>

                  <TextField
                    className="m-1"
                    variant="standard"
                    placeholder={"Unit"}
                    label={"Unit"}
                    value={selectedAxis.unit ?? ""}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange ("unit", event.target.value)}/>

                  <TextField
                    className="m-1"
                    label="Minimum"
                    variant="standard"
                    size="small"
                    value={selectedAxis.minimum}
                    type="number"
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange ("minimum", parseFloat(event.target.value))}
                  />

                  <TextField
                    className="m-1"
                    label="Maximum"
                    variant="standard"
                    size="small"
                    value={selectedAxis.maximum}
                    type="number"
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange ("maximum", parseFloat(event.target.value))}
                  />
                </div>
              }
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AxisConfig;
